import {
  Home,
  Target,
  SlidersHorizontal,
  BarChart3,
  Route as RouteIcon,
  Send,
  MapPin,
  Trophy,
  Users,
  Radio,
  FolderHeart,
  Puzzle,
  FileText,
  ClipboardCheck,
  Sparkles,
  PackageCheck,
  ArrowUpRight,
  AlertTriangle,
  Scale,
  Inbox,
  FileQuestion,
} from "lucide-react";
import { Link } from "react-router-dom";
import { PageLayout } from "@/components/PageLayout";
import { PageHero } from "@/components/blocks/PageHero";
import { SectionHeading } from "@/components/blocks/SectionHeading";
import { CTABand } from "@/components/blocks/CTABand";
import { HeroCard } from "@/components/blocks/HeroCard";
import { ExistingSponsorship } from "@/components/blocks/ExistingSponsorship";
import { Steps } from "@/components/blocks/Steps";
import { Reveal } from "@/components/Reveal";
import { COMPANY_FEATURES } from "@/lib/features";
import { ROUTE_DEMO, waitlistFor } from "@/lib/brand";
import { EXTRA_SPONSORSHIP_FEE, euro } from "@/lib/pricing";
import { CUSTOM_DELIVERABLES, CUSTOM_DELIVERABLES_TITLE, SOURCES } from "@/lib/measurement";
import { buildDashboard, createSetup, CLUB } from "@/components/demo/demoData";
import { Highlights, Legend } from "@/components/demo/DemoDashboard";
import { SourceBadge } from "@/components/demo/ui";

// Ausschnitt aus der Demo: dieselben Beispielwerte wie dort, damit Seite und
// Demo sich nicht widersprechen.
const SNAPSHOT = buildDashboard(createSetup("baeckerei"));

// Die Filtersuche hat mit „Finden" bereits einen eigenen Abschnitt und steht
// deshalb nur kompakt am Ende des Rasters statt als volle Karte.
const PRIMARY = COMPANY_FEATURES.filter(
  (f) => !f.secondaryFor?.includes("company")
);
const SECONDARY = COMPANY_FEATURES.filter((f) =>
  f.secondaryFor?.includes("company")
);

const SIDEBAR_ITEMS = [
  { id: "top", label: "Start", icon: Home },
  { id: "ausgangslage", label: "Was heute fehlt", icon: AlertTriangle },
  { id: "warum", label: "Warum MatchGate", icon: Target },
  { id: "messbarkeit", label: "Wirkung belegen", icon: BarChart3 },
  { id: "suche", label: "Vereine finden", icon: SlidersHorizontal },
  { id: "funktionen", label: "Was ihr bekommt", icon: PackageCheck },
  { id: "ablauf", label: "So läuft es ab", icon: RouteIcon },
  { id: "cta", label: "Loslegen", icon: Send },
];

const GAPS = [
  {
    icon: Scale,
    title: "Angebote lassen sich nicht vergleichen",
    text: "Jeder Verein schickt etwas anderes: ein PDF, eine Preisliste, eine Mail mit drei Sätzen. Was davon mehr bringt, bleibt Einschätzung.",
  },
  {
    icon: Inbox,
    title: "Anfragen kommen ungefiltert",
    text: "Im Postfach landen Vereine, die überhaupt nicht zur Marke passen. Das Aussortieren kostet Zeit, und die passenden gehen dabei unter.",
  },
  {
    icon: FileQuestion,
    title: "Am Jahresende fehlt der Beleg",
    text: "Ob das Engagement etwas bewirkt hat, lässt sich nicht sagen. Damit ist jede Verlängerung eine Wette.",
  },
];

const ARGUMENTS = [
  {
    icon: Target,
    title: "Regional wirken, nicht streuen",
    text: "Ein Verein bringt euch keine anonyme Impression, sondern Menschen aus eurem Einzugsgebiet - genau dort, wo eure Kundinnen und Kunden leben und einkaufen.",
  },
  {
    icon: Sparkles,
    title: "Angebote endlich nebeneinander",
    text: "Alle Vereine liefern dieselben Angaben in derselben Struktur. Erst dadurch lässt sich überhaupt abwägen, welches Engagement mehr bringt.",
  },
  {
    icon: FileText,
    title: "Belegbar gegenüber der Geschäftsführung",
    text: "Am Ende steht kein Erinnerungsfoto, sondern ein Bericht mit Belegen und Zahlen, der auch in der Budgetrunde trägt.",
  },
];

const CRITERIA = [
  {
    icon: MapPin,
    label: "Region",
    text: "Ort, Umkreis oder Bundesland - bis auf den Stadtteil genau.",
  },
  {
    icon: Trophy,
    label: "Sportart",
    text: "Von Fußball über Handball bis Rudern, inklusive Breiten- und Leistungssport.",
  },
  {
    icon: Users,
    label: "Zielgruppe",
    text: "Alter, Familienanteil und Zusammensetzung der Vereinsgemeinschaft.",
  },
  {
    icon: Radio,
    label: "Reichweite",
    text: "Mitglieder, Zuschauerschnitt und Follower je Kanal, jeweils mit Herkunft ausgewiesen.",
  },
  {
    icon: FolderHeart,
    label: "Projektart",
    text: "Trikots, Jugendarbeit, Infrastruktur, Events oder Nachhaltigkeit.",
  },
];

const RESULTS = [
  {
    name: "TSV Musterstadt",
    meta: "Fußball · 1.240 Mitglieder · 8 km",
    tags: ["Jugendarbeit", "Familien"],
    score: 94,
  },
  {
    name: "HSG Nordufer",
    meta: "Handball · 640 Mitglieder · 14 km",
    tags: ["Nachwuchs", "Halle"],
    score: 88,
  },
  {
    name: "RC Elbe 1897",
    meta: "Rudern · 310 Mitglieder · 21 km",
    tags: ["Nachhaltigkeit", "Events"],
    score: 81,
  },
];

/** Die drei Ebenen aus Sicht des Unternehmens, mit konkreten Beispielen. */
const LEVEL_DETAILS = [
  {
    no: "1",
    title: "Geliefert",
    lead: "Hat der Verein geliefert, was vereinbart war?",
    points: [
      "Bande, Banner und Trikot per Foto belegt",
      "Durchsage und Heft am Spieltag abgehakt",
      "Beiträge und Website-Nennung automatisch geprüft",
      "Fotos für eure Kanäle zum Herunterladen",
    ],
  },
  {
    no: "2",
    title: "Gesehen",
    lead: "Wer hat euer Engagement wahrgenommen?",
    points: [
      "Zuschauer je Heimspiel, vom Verein eingetragen",
      "Aufrufe und Interaktionen eurer Partnerbeiträge",
      "Kontakte vor Ort, offen hochgerechnet",
      "Derby oder Regen als Erklärung für Ausreißer",
    ],
  },
  {
    no: "3",
    title: "Bewirkt",
    lead: "Was hat das Sponsoring ausgelöst?",
    points: [
      "Scans eurer QR-Codes, je Platzierung getrennt",
      "Klicks auf eure Links, je Kanal getrennt",
      "Einlösungen, Bewerbungen und Abschlüsse, die ihr meldet",
      "Ihr entscheidet, was davon der Verein sieht",
    ],
  },
];

const STEPS = [
  {
    no: "01",
    title: "Profil anlegen",
    text: "Region, Budgetrahmen, Zielgruppe und Themen, die zu eurer Marke passen.",
  },
  {
    no: "02",
    title: "Vereine vergleichen",
    text: "Passende Profile und offene Projekte nebeneinander, mit denselben Angaben.",
  },
  {
    no: "03",
    title: "Direkt anfragen",
    text: "Kontakt zur richtigen Ansprechperson, Konditionen transparent verhandeln.",
  },
  {
    no: "04",
    title: "Sponsoring einrichten",
    text: "Leistungen festhalten, Schwerpunkt wählen, beide Seiten bestätigen. QR-Codes und Links entstehen dabei automatisch.",
  },
  {
    no: "05",
    title: "Wirkung verfolgen",
    text: "Dashboard über die gesamte Laufzeit, Bericht zum Abschluss der Saison.",
  },
];

/** Rechte Spalte im Kopfbereich. */
const AT_A_GLANCE = [
  {
    icon: SlidersHorizontal,
    title: "Passende Vereine finden",
    text: "Nach Region, Sportart, Zielgruppe und Reichweite, sortiert nach MatchScore.",
  },
  {
    icon: ClipboardCheck,
    title: "Klar vereinbart",
    text: "Leistungen, QR-Codes und Links beim Abschluss festgehalten.",
  },
  {
    icon: BarChart3,
    title: "Nachprüfbar",
    text: "Jede Leistung belegt, jede Zahl mit Herkunft.",
  },
  {
    icon: FileText,
    title: "Intern vorlegbar",
    text: "Ein Bericht für Geschäftsführung und Budgetrunde.",
  },
];

export default function ForCompanies() {
  return (
    <PageLayout
      sidebarItems={SIDEBAR_ITEMS}
      title="Für Unternehmen"
      testId="for-companies-main"
    >
      <PageHero
        eyebrow="Für Unternehmen"
        titleLines={["Sponsoring mit", "Zahlen statt"]}
        titleAccent="Bauchgefühl."
        lead="Findet Vereine und Projekte, die wirklich zu eurer Marke passen: nach Region, Sportart, Zielgruppe und Reichweite. Und verfolgt anschließend über die gesamte Laufzeit, was aus dem Engagement geworden ist."
        primary={{ label: "Platz vormerken", to: waitlistFor("unternehmen") }}
        secondary={{ label: "Wie wir messen", href: "#messbarkeit" }}
        chipsLabel="Für Unternehmen mit dabei"
        chips={COMPANY_FEATURES.map((f) => f.title)}
      >
        <HeroCard kicker="Für euer Unternehmen auf einen Blick" items={AT_A_GLANCE} />
      </PageHero>

      {/* Was heute fehlt */}
      <section
        id="ausgangslage"
        data-testid="companies-gaps-section"
        className="relative py-24 lg:py-32"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-sticky">
              <SectionHeading
                eyebrow="Was heute fehlt"
                title="Entscheiden, ohne etwas "
                accent="in der Hand"
                tail=" zu haben."
                lead="Regionales Sponsoring scheitert selten am Budget. Es scheitert daran, dass sich Angebote nicht vergleichen lassen und am Jahresende keine Zahl dazu vorliegt."
                className="max-w-md mb-0"
              />
            </div>
          </div>

          <div className="lg:col-span-7 flex flex-col gap-5">
            {GAPS.map((gap, i) => {
              const Icon = gap.icon;
              return (
                <Reveal key={gap.title} delay={i * 0.06}>
                  <div className="group flex gap-6 rounded-3xl border border-white/10 backdrop-blur-xl p-7 md:p-8 hover:border-white/20 transition-colors duration-500">
                    <span className="flex items-center justify-center w-12 h-12 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Icon className="w-5 h-5 text-brand-blue" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-2">
                        {gap.title}
                      </h3>
                      <p className="text-base leading-relaxed text-white/55">
                        {gap.text}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Warum */}
      <section
        id="warum"
        data-testid="companies-why-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-0 left-1/3 w-[620px] h-[420px] rounded-full bg-brand-blue/10 blur-[160px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Warum MatchGate"
            title="Regionales Sponsoring, endlich "
            accent="sauber aufbereitet"
            tail="."
            lead="Genau hier setzt MatchGate an. Wenn beide Seiten dieselben Angaben in derselben Struktur liefern, lässt sich regionales Sponsoring vergleichen, verhandeln und am Ende belegen."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {ARGUMENTS.map((a, i) => {
              const Icon = a.icon;
              return (
                <Reveal key={a.title} delay={i * 0.07}>
                  <div className="group relative h-full rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500 overflow-hidden">
                    <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                      <Icon className="w-5 h-5 text-brand-orange" />
                    </span>
                    <h3 className="font-display font-bold text-2xl text-white mb-3 hyphens-auto break-words">
                      {a.title}
                    </h3>
                    <p className="text-base leading-relaxed text-white/55">
                      {a.text}
                    </p>
                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Messbarkeit */}
      <section
        id="messbarkeit"
        data-testid="companies-metrics-section"
        className="relative py-24 lg:py-32 border-y border-white/[0.06]"
      >
        <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
          <div className="absolute top-1/3 right-0 w-[600px] h-[460px] rounded-full bg-brand-orange/10 blur-[170px]" />
        </div>

        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Wirkung belegen"
            title="Der Kern von MatchGate: Sponsoring wird "
            accent="belegbar"
            tail="."
            lead="Jedes Sponsoring wird beim Abschluss eingerichtet: welche Leistungen vereinbart sind, welche QR-Codes und Links dazugehören und worauf ihr den größten Wert legt. Ab dann läuft das Dashboard über die ganze Saison mit. Was sich messen lässt, wird gemessen. Was nur eingetragen werden kann, ist genau so gekennzeichnet."
          />

          <Reveal>
            <div className="relative overflow-hidden rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-10 mb-6">
              <div className="pointer-events-none absolute -top-24 -right-16 w-80 h-80 rounded-full bg-brand-blue/15 blur-[110px]" />
              <div className="relative flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
                <div>
                  <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/40">
                    Ausschnitt aus der Demo
                  </span>
                  <div className="mt-3 font-display font-bold text-2xl md:text-3xl text-white">
                    {SNAPSHOT.sponsor.name} <span className="text-white/30">×</span> {CLUB.name}
                  </div>
                </div>
                <Legend />
              </div>
              <div className="relative">
                <Highlights items={SNAPSHOT.highlights} />
              </div>
              <div className="relative mt-8 pt-7 border-t border-white/10 flex flex-col sm:flex-row sm:items-center gap-4">
                <Link
                  to={ROUTE_DEMO}
                  data-testid="companies-demo-link"
                  className="group inline-flex shrink-0 items-center gap-2 rounded-full px-6 py-3 text-sm font-bold text-white bg-gradient-to-r from-brand-orange to-brand-blue shadow-[0_0_30px_-8px_rgba(255,107,0,0.7)] hover:shadow-[0_0_40px_-6px_rgba(0,122,255,0.8)] hover:scale-[1.03] transition-all duration-300"
                >
                  Dashboard in der Demo ansehen
                  <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                </Link>
                <p className="text-xs leading-relaxed text-white/35">
                  Beispielwerte, realistisch für einen Kreisligisten mit rund 150
                  Zuschauern je Heimspiel. In der Demo stellt ihr selbst ein
                  Sponsoring zusammen und seht das ganze Dashboard.
                </p>
              </div>
            </div>
          </Reveal>

          <div className="grid gap-5 md:gap-6 lg:grid-cols-3">
            {LEVEL_DETAILS.map((l, i) => (
              <Reveal key={l.title} delay={i * 0.06}>
                <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                  <div className="absolute -right-3 -top-6 font-display font-black text-[6rem] leading-none text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none">
                    {l.no}
                  </div>
                  <div className="relative">
                    <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-brand-orange mb-3">
                      Ebene {l.no}
                    </span>
                    <h3 className="font-display font-bold text-2xl text-white">
                      {l.title}
                    </h3>
                    <p className="mt-2 text-base leading-relaxed text-white/55">
                      {l.lead}
                    </p>
                    <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5">
                      {l.points.map((p) => (
                        <li key={p} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-gradient-to-r from-brand-orange to-brand-blue shrink-0" />
                          <span className="text-sm leading-relaxed text-white/70">{p}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                </div>
              </Reveal>
            ))}
          </div>

          <div className="mt-6 grid gap-5 md:gap-6 lg:grid-cols-12">
            <Reveal className="lg:col-span-7">
              <div className="h-full rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
                <span className="block text-[11px] uppercase tracking-[0.2em] font-bold text-white/40">
                  Woher jede Zahl kommt
                </span>
                <div className="mt-6 grid gap-6 sm:grid-cols-3">
                  {SOURCES.map((s) => (
                    <div key={s.kind}>
                      <SourceBadge kind={s.kind} proof={s.proof} />
                      <p className="mt-3 text-sm leading-relaxed text-white/55">{s.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-5" delay={0.08}>
              <div className="h-full rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10">
                <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10 mb-6">
                  <Puzzle className="w-5 h-5 text-brand-blue" />
                </span>
                <h3 className="font-display font-bold text-xl md:text-2xl text-white mb-3">
                  {CUSTOM_DELIVERABLES_TITLE}
                </h3>
                <p className="text-sm leading-relaxed text-white/60">{CUSTOM_DELIVERABLES}</p>
              </div>
            </Reveal>
          </div>

          <ExistingSponsorship
            className="mt-6"
            title="Ihr fördert schon einen Verein?"
            text={`Dann holt ihn mit auf MatchGate. Ihr richtet das bestehende Sponsoring gemeinsam ein und bekommt ab dann dasselbe Dashboard, auch mitten in der Saison. Für Sponsorings, die schon vor MatchGate bestanden, fällt keine Provision an. So seht ihr endlich auch bei Partnerschaften, die längst laufen, was sie bringen. Wie immer braucht jede Seite dafür ihr Abo: Ein Sponsoring ist darin enthalten, jedes weitere kostet beim Einrichten einmalig ${euro(EXTRA_SPONSORSHIP_FEE)}.`}
          />
        </div>
      </section>

      {/* Suche */}
      <section
        id="suche"
        data-testid="companies-search-section"
        className="relative py-24 lg:py-32"
      >
        <div className="px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-6">
            <SectionHeading
              eyebrow="Vereine finden"
              title="Fünf Kriterien, die aus tausend Vereinen "
              accent="eure drei"
              tail=" machen."
              className="max-w-xl mb-10"
            />

            <div className="flex flex-col gap-4">
              {CRITERIA.map((c, i) => {
                const Icon = c.icon;
                return (
                  <Reveal key={c.label} delay={i * 0.05}>
                    <div className="group flex items-start gap-5 rounded-2xl border border-white/10 backdrop-blur-xl px-6 py-5 hover:border-white/20 transition-colors duration-500">
                      <span className="flex items-center justify-center w-10 h-10 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                        <Icon className="w-[18px] h-[18px] text-brand-blue" />
                      </span>
                      <div>
                        <h3 className="font-display font-bold text-lg text-white">
                          {c.label}
                        </h3>
                        <p className="mt-1 text-sm leading-relaxed text-white/55">
                          {c.text}
                        </p>
                      </div>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="lg:sticky lg:top-sticky">
              <Reveal delay={0.1}>
                <div className="rounded-[2rem] border border-white/10 backdrop-blur-xl p-7 md:p-8">
                  <div className="flex items-baseline justify-between mb-6">
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/40">
                      Treffer
                    </span>
                    <span className="text-xs text-white/35">
                      sortiert nach MatchScore
                    </span>
                  </div>

                  <div className="flex flex-col gap-4">
                    {RESULTS.map((r) => (
                      <div
                        key={r.name}
                        className="group rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:border-white/25 hover:bg-white/[0.05] transition-all duration-400"
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div>
                            <div className="font-display font-bold text-lg text-white">
                              {r.name}
                            </div>
                            <div className="mt-0.5 text-xs text-white/45">
                              {r.meta}
                            </div>
                          </div>
                          <div className="text-right shrink-0">
                            <div className="font-display font-black text-2xl gradient-text">
                              {r.score}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider text-white/35">
                              MatchScore
                            </div>
                          </div>
                        </div>

                        <div className="mt-4 flex items-center gap-2">
                          {r.tags.map((t) => (
                            <span
                              key={t}
                              className="rounded-full border border-white/10 bg-white/[0.03] px-2.5 py-0.5 text-[11px] text-white/55"
                            >
                              {t}
                            </span>
                          ))}
                        </div>

                        <div className="mt-4 h-1 w-full rounded-full bg-white/[0.07] overflow-hidden">
                          <div
                            style={{ width: `${r.score}%` }}
                            className="h-full rounded-full bg-gradient-to-r from-brand-orange to-brand-blue"
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-6 text-xs text-white/30">
                    Beispielhafte Darstellung der Trefferliste.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Funktionen */}
      <section
        id="funktionen"
        data-testid="companies-features-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="Was ihr bekommt"
            title="Sechs Funktionen - alle im "
            accent="Abo enthalten"
            tail="."
            lead="Keine Funktionsstufen, keine Zusatzpakete. Vom Profil bis zum Analytics-Dashboard steht euch ab dem ersten Tag alles zur Verfügung. Pitch-Generator und Media-Kit-Templates sind Werkzeuge, mit denen Vereine ihre Unterlagen und ihren Auftritt gestalten - davon profitiert ihr, ohne sie selbst bedienen zu müssen."
          />

          <div className="grid gap-5 md:gap-6 lg:grid-cols-2">
            {PRIMARY.map((f, i) => {
              const Icon = f.icon;
              const no = String(i + 1).padStart(2, "0");
              return (
                <Reveal key={f.id} delay={(i % 2) * 0.05}>
                  <div className="group relative h-full overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 hover:border-white/20 transition-colors duration-500">
                    <div className="absolute -right-4 -top-8 font-display font-black text-[7rem] leading-none text-white/[0.03] group-hover:text-white/[0.06] transition-colors duration-500 select-none">
                      {no}
                    </div>

                    <div className="relative">
                      <div className="flex items-center gap-3 mb-5">
                        <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-white/[0.06] border border-white/10">
                          <Icon className="w-5 h-5 text-brand-orange" />
                        </span>
                        <span className="rounded-full border border-white/10 px-3 py-1 text-[11px] uppercase tracking-[0.15em] font-bold text-brand-orange">
                          {f.title}
                        </span>
                      </div>

                      <h3 className="font-display font-bold text-2xl text-white mb-3 hyphens-auto break-words">
                        {f.company.headline}
                      </h3>
                      <p className="text-base leading-relaxed text-white/55">
                        {f.company.text}
                      </p>

                      <ul className="mt-6 pt-6 border-t border-white/10 space-y-2.5">
                        {f.company.points.map((p) => (
                          <li key={p} className="flex items-center gap-3">
                            <span className="w-1.5 h-1.5 rounded-full bg-gradient-to-r from-brand-orange to-brand-blue shrink-0" />
                            <span className="text-sm text-white/70">{p}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                  </div>
                </Reveal>
              );
            })}

            {SECONDARY.map((f) => {
              const Icon = f.icon;
              return (
                <Reveal key={f.id} delay={0.05}>
                  <div className="h-full flex items-center gap-5 rounded-3xl border border-white/10 border-dashed backdrop-blur-xl px-8 py-7">
                    <span className="flex items-center justify-center w-11 h-11 shrink-0 rounded-xl bg-white/[0.06] border border-white/10">
                      <Icon className="w-5 h-5 text-white/50" />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-lg text-white">
                        {f.title}
                      </h3>
                      <p className="mt-1 text-sm leading-relaxed text-white/50">
                        {f.company.text}
                        {f.company.ref && (
                          <>
                            {" "}
                            <a
                              href={f.company.ref.href}
                              className="text-white/70 underline decoration-white/20 underline-offset-4 hover:text-white hover:decoration-white/50 transition-colors duration-300"
                            >
                              {f.company.ref.label}
                            </a>
                            {f.company.ref.tail}
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* Ablauf */}
      <section
        id="ablauf"
        data-testid="companies-steps-section"
        className="relative py-24 lg:py-32 border-t border-white/[0.06]"
      >
        <div className="px-6 md:px-10 lg:px-16">
          <SectionHeading
            eyebrow="So läuft es ab"
            title="Fünf Schritte vom Profil bis zum "
            accent="Bericht"
            tail="."
          />

          <Steps steps={STEPS} />
        </div>
      </section>

      <CTABand
        title="Findet die Vereine, die zu euch "
        accent="passen"
        tail="."
        lead="Sagt uns, welche Region und welche Zielgruppe für euch zählen - wir zeigen euch, was dort möglich ist."
        primary={{ label: "Platz vormerken", to: waitlistFor("unternehmen") }}
        secondary={{ label: "Was es kostet", to: "/preisstruktur" }}
      />
    </PageLayout>
  );
}
